import Portal from "@common/controls/Portal/component";
import { type WithClickOutsideHOCButtonBeahviour } from "@common/hocs/withClickOutside";
import { useClickOutside } from "@common/lib/hooks";
import classnames from "classnames";
import { isEqual, isFunction } from "lodash";
import * as React from "react";
import { type AlignContent, ContentAlign, type ContentPosition, rectToPosition } from "./lib";
import "./style.less";

export interface WithDropdownComponentAddedProps {
  align?: string | ContentAlign | AlignContent;
  cannotClose?: boolean;
  maxHeight?: number | string;
  maxWidth?: number;
  dropdownClassName?: string;
  dropdownName?: string;
  isAnimateable?: boolean;
  keepOpenIfMouseOver?: boolean;
  onClose?: () => void;
  zIndex?: number;
  onOpen?: () => void;

  onBlur?: () => void;
  onFocus?: () => void;

  // new
  behaviour?: "hide" | "close"; // TODO: remove

  ignoreSelector?: string; // TODO: remove
}

interface SharedProps {
  isOpen: boolean;
  toggleOpen: () => void;
  open: () => void;
  close: () => void;
  maxHeight?: number | string;
}

export type WithDropdownToggleProps = SharedProps & {
  toggleRef: React.RefObject<any>;
};

export type WithDropdownCmpProps = SharedProps & {
  isOpen: boolean;
};

type ContentWrapProps = Partial<WithDropdownToggleProps> &
  React.HTMLProps<HTMLDivElement> & {
    cannotClose?: boolean;
    onResized: () => void;
  };
const ContentWrap = React.forwardRef(
  (
    { close, cannotClose, isOpen, open, onResized, toggleRef, ...props }: ContentWrapProps,
    ref: React.RefObject<any>
  ) => {
    useClickOutside("dropdown", {
      callback: close!,
      cannotClose: cannotClose,
      ignoreRefs: [toggleRef, ref]
    });

    React.useEffect(() => {
      const resizeObserver = new ResizeObserver(onResized);
      ref.current && resizeObserver.observe(ref.current);

      return () => {
        ref.current && resizeObserver.unobserve(ref.current);
        resizeObserver.disconnect();
      };
    }, [ref.current]);

    return (
      <Portal>
        <div
          ref={ref}
          {...props}
          onClick={(e) => {
            e.stopPropagation();
            props.onClick && props.onClick(e);
          }}
        />
      </Portal>
    );
  }
);

export type WithDropdownHOCOptions = {
  buttonBehaviour?: WithClickOutsideHOCButtonBeahviour;
  disableKeyboardToggle?: boolean;
  dontFocusContentOnOpen?: boolean;
  ignoreSelector?: string | string[]; // TODO: remove or make use of it
  scrollBehaviour?: "close" | "keep" | "none";
};
const withDropdownHOC = <ToggleOriginalProps extends {}, CmpOriginalProps extends {} = ToggleOriginalProps>(
  ToggleCmp: React.ComponentType<ToggleOriginalProps & WithDropdownToggleProps>,
  Cmp: React.ComponentType<CmpOriginalProps & WithDropdownCmpProps>,
  options?: WithDropdownHOCOptions
) => {
  type FinalProps = CmpOriginalProps & ToggleOriginalProps & WithDropdownComponentAddedProps;
  type State = {
    isCloseScheduled: boolean;
    isOpen: boolean;
    position?: ContentPosition;
  };

  return class WithDropdownHOC extends React.PureComponent<Omit<FinalProps, "toggleRef">, State> {
    _contentWrapRef: React.RefObject<any> = React.createRef();
    _toggleRef: React.RefObject<any> = React.createRef();
    private isMouseOverContent = false;

    public state: State = {
      isCloseScheduled: false,
      isOpen: false
    };

    public componentDidMount() {
      this._updateRects();

      const toggleEl = this._getEl(this._toggleRef);
      toggleEl && toggleEl.addEventListener("keydown", this._onToggleKeyDown);

      window.addEventListener("resize", this._onResize);
    }

    public componentDidUpdate(prevProps: FinalProps, prevState: State): void {
      if (isEqual(this.state.position, prevState.position)) {
        this._updateRects();
      }

      if (prevState.isOpen !== this.state.isOpen) {
        if (this.state.isOpen) {
          options?.scrollBehaviour !== "none" && window.addEventListener("scroll", this._onScroll, true);

          if (!options?.dontFocusContentOnOpen) {
            const contentEl = this._contentWrapRef.current as HTMLElement;
            contentEl && contentEl.focus();
          }
        } else {
          options?.scrollBehaviour !== "none" && window.removeEventListener("scroll", this._onScroll, true);
        }
      }
    }

    public componentWillUnmount() {
      if (this.state.isOpen) {
        window.removeEventListener("scroll", this._onScroll);
      }

      const toggleEl = this._getEl(this._toggleRef);
      toggleEl && toggleEl.removeEventListener("keydown", this._onToggleKeyDown);

      window.removeEventListener("resize", this._onResize);
    }

    private _onResize = () => {
      setTimeout(this._updateRects, 30);
    };

    private _onToggleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === " " || e.key === "Enter") && !options?.disableKeyboardToggle) {
        this._toggleOpen();
      }
    };

    _onScroll = (e: Event) => {
      const target = e.target as HTMLElement; // can be document
      if (target.classList?.contains("dropdown_container") || (target.closest && target.closest(".dropdown_container")))
        return;

      this._updateRects();
    };

    public render() {
      // TODO: isAnimateable
      const { isAnimateable, cannotClose, dropdownClassName, zIndex, dropdownName, maxHeight, maxWidth, ...props } =
        this.props;
      const { isOpen, position } = this.state;

      const sharedProps: SharedProps = {
        close: this._close,
        isOpen,
        open: this._open,
        toggleOpen: this._toggleOpen
      };

      const { align, ...positionCSS } = position || {};
      const style: React.CSSProperties = {
        ...positionCSS,
        position: "fixed",
        pointerEvents: isOpen ? "all" : "none",
        zIndex,
        maxHeight: maxHeight ?? 300,
        maxWidth: Math.min(maxWidth ?? 1000000, window.innerWidth)
      };
      return (
        <>
          <ToggleCmp toggleRef={this._toggleRef} {...(props as ToggleOriginalProps)} {...sharedProps} tabIndex={0} />

          {isOpen && (
            <ContentWrap
              ref={this._contentWrapRef}
              cannotClose={cannotClose}
              className={classnames(
                dropdownClassName,
                { dropdown_container: !isAnimateable, open: true },
                align?.toLowerCase()
              )}
              toggleRef={this._toggleRef}
              style={style}
              onMouseEnter={this._onContentMouseEnter}
              onMouseLeave={this._onContentMouseLeave}
              isOpen={isOpen}
              close={this._close}
              onResized={this._updateRects}
              data-name={dropdownName}
            >
              <div className="pointer" />
              <div className="content">
                <Cmp {...(props as CmpOriginalProps)} {...sharedProps} />
              </div>
            </ContentWrap>
          )}
        </>
      );
    }

    _updateRects = () => {
      if (!this.state.isOpen) return;

      const contentWrapEl = this._getEl(this._contentWrapRef);
      if (!contentWrapEl) return;
      const toggleEl = this._getEl(this._toggleRef);
      if (!toggleEl) return;

      const contentRect = contentWrapEl.getBoundingClientRect();
      const toggleRect = toggleEl.getBoundingClientRect();
      const position =
        contentRect && toggleRect
          ? isFunction(this.props.align)
            ? this.props.align(toggleRect, contentRect)
            : rectToPosition(toggleRect, contentRect, this.props.align)
          : undefined;

      if (!isEqual(this.state.position, position)) {
        this.setState({ position });
      }
    };

    _onContentMouseEnter = () => {
      this.isMouseOverContent = true;
    };

    _onContentMouseLeave = () => {
      this.isMouseOverContent = false;
      this.state.isCloseScheduled && this._close();
    };

    _getEl = (ref: React.RefObject<any>): HTMLElement => {
      return ref.current as HTMLElement;
    };
    _close = () =>
      setTimeout(() => {
        if (this.props.keepOpenIfMouseOver && this.isMouseOverContent) {
          this.setState({ isCloseScheduled: true });
          return;
        }

        this.setState({ isOpen: false }, () => {
          this.props.onClose && this.props.onClose();
          this.props.onBlur && this.props.onBlur();
        });
      }, 0);
    _open = () => {
      this.setState({ isCloseScheduled: false, isOpen: true }, () => {
        this.props.onFocus && this.props.onFocus();
      });
      this.props.onOpen && this.props.onOpen();
    };
    _toggleOpen = () => (this.state.isOpen ? this._close() : this._open());
  };
};

export default withDropdownHOC;
